/**
 * Shared client-side field validation for every public form.
 *
 * Mirrors the server-side rules so users get instant, specific feedback — and,
 * importantly, catches the classic mix-ups (an email typed into the phone box,
 * a phone number typed into the email box, garbage in a numeric field) before
 * the request is ever sent. The PHP handlers re-check the same rules, so this
 * is a UX layer, not the security boundary.
 */

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_PATTERN = /^[+()0-9][0-9+()\-.\s]{4,24}$/;

export interface FieldProblem {
  field: string;
  message: string;
}

export function isEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

/** Cheap signal that the value is an email address, used for cross-field checks. */
export function looksLikeEmail(value: string): boolean {
  return value.includes("@");
}

export function countDigits(value: string): number {
  return (value.match(/\d/g) || []).length;
}

/**
 * A phone number: optional leading +, 7–15 digits total, and never an email.
 * Deliberately permissive on spacing/formatting (+91 81235 84806, (0821) 234…).
 */
export function isPhone(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  if (looksLikeEmail(v)) return false;
  if (!PHONE_PATTERN.test(v)) return false;
  const digits = countDigits(v);
  return digits >= 7 && digits <= 15;
}

export interface ContactFieldOptions {
  email?: string;
  phone?: string;
  emailRequired?: boolean;
  phoneRequired?: boolean;
}

/** Returns every problem found; empty array means the contact fields are clean. */
export function validateContactFields(options: ContactFieldOptions): FieldProblem[] {
  const problems: FieldProblem[] = [];
  const email = (options.email ?? "").trim();
  const phone = (options.phone ?? "").trim();

  if (options.emailRequired && !email) {
    problems.push({ field: "email", message: "Email is required." });
  } else if (email) {
    if (looksLikeEmail(email) && !isEmail(email)) {
      problems.push({ field: "email", message: "Please enter a valid email address (e.g. name@example.com)." });
    } else if (!isEmail(email)) {
      problems.push({ field: "email", message: "Please enter a valid email address." });
    }
  }

  if (options.phoneRequired && !phone) {
    problems.push({ field: "phone", message: "Phone number is required." });
  } else if (phone) {
    if (looksLikeEmail(phone)) {
      problems.push({ field: "phone", message: "That looks like an email address — please enter a phone number." });
    } else if (!isPhone(phone)) {
      problems.push({ field: "phone", message: "Please enter a valid phone number (7–15 digits)." });
    }
  }

  return problems;
}

/** Validates the email/phone pair read straight from a submitted form. */
export function validateFormData(
  data: FormData,
  required: { emailRequired?: boolean; phoneRequired?: boolean } = {},
): FieldProblem[] {
  return validateContactFields({
    email: String(data.get("email") ?? ""),
    phone: String(data.get("phone") ?? ""),
    ...required,
  });
}

/** Convenience: the first problem message, or "" when everything is valid. */
export function firstProblem(problems: FieldProblem[]): string {
  return problems.length > 0 ? problems[0].message : "";
}